import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle2, AlertCircle, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Pagina pubblica per la firma del registro presenze tramite token.
// Accessibile senza autenticazione: /sign-register/:token
export default function SignRegisterPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(true);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    axios.get(`/api/public/sign-register/${token}`)
      .then((r) => {
        setInfo(r.data);
        if (r.data.participant?.already_signed) setDone(true);
      })
      .catch((e) => setError(e.response?.data?.detail || 'Link non valido o scaduto'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!canvasRef.current || done) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    // Resize for retina
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0a1f4e';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);

    const getPos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const start = (e) => {
      e.preventDefault();
      drawing.current = true;
      const { x, y } = getPos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const move = (e) => {
      if (!drawing.current) return;
      e.preventDefault();
      const { x, y } = getPos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
    };
    const end = () => { drawing.current = false; };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [info, done]);

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    const r = c.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, r.width, r.height);
  };

  const isCanvasEmpty = () => {
    const c = canvasRef.current;
    if (!c) return true;
    const ctx = c.getContext('2d');
    const data = ctx.getImageData(0, 0, c.width, c.height).data;
    // count non-white pixels
    let nonWhite = 0;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) {
        nonWhite++;
        if (nonWhite > 50) return false;
      }
    }
    return true;
  };

  const submitSignature = async () => {
    if (!consent) { toast.error('Devi accettare il consenso al trattamento'); return; }
    if (isCanvasEmpty()) { toast.error('Firma vuota. Disegna la tua firma nel riquadro.'); return; }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      await axios.post(`/api/public/sign-register/${token}`, {
        signature_b64: dataUrl,
        consent: true,
      });
      setDone(true);
      toast.success('Firma registrata. Grazie!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio firma');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" /> Link non valido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700">{error}</p>
            <p className="text-xs text-gray-500 mt-3">Contatta il tuo docente per ricevere un nuovo link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 p-4 flex items-start justify-center">
      <Card className="max-w-2xl w-full mt-8" data-testid="public-sign-card">
        <CardHeader className="bg-blue-900 text-white rounded-t-md">
          <CardTitle className="text-lg">Firma del registro presenze</CardTitle>
          <p className="text-sm text-blue-100">{info.course_name} · {info.course_date}</p>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="bg-blue-50 rounded p-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><strong>Discente:</strong> {info.participant.name}</div>
              <div><strong>CF:</strong> <span className="font-mono">{info.participant.fiscal_code}</span></div>
              {(info.ora_inizio || info.ora_fine) && (
                <div><strong>Orario:</strong> {info.ora_inizio || '—'}{info.ora_fine ? ` - ${info.ora_fine}` : ''}</div>
              )}
              {info.ore_totali && <div><strong>Ore:</strong> {info.ore_totali}</div>}
              {info.docente_nome && <div className="col-span-2"><strong>Docente:</strong> {info.docente_nome}</div>}
              {info.luogo_corso && <div className="col-span-2"><strong>Luogo:</strong> {info.luogo_corso}{info.aula ? ` — ${info.aula}` : ''}</div>}
              {info.tipologia_corso && <div className="col-span-2"><strong>Tipologia:</strong> {info.tipologia_corso}</div>}
              {info.codice_progetto && <div className="col-span-2"><strong>Codice progetto:</strong> {info.codice_progetto}</div>}
            </div>
          </div>

          {done ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-6 text-center" data-testid="public-sign-done">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
              <p className="font-semibold text-emerald-800">Firma registrata correttamente</p>
              <p className="text-sm text-emerald-700 mt-1">Grazie per aver firmato il registro del corso.</p>
              {info.participant.signed_at && (
                <p className="text-xs text-gray-500 mt-2">Firmato il {new Date(info.participant.signed_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              )}
            </div>
          ) : (
            <>
              <div>
                <p className="text-sm font-semibold mb-2">Firma qui sotto (con dito o mouse):</p>
                <div className="border-2 border-dashed border-gray-300 rounded overflow-hidden bg-white">
                  <canvas
                    ref={canvasRef}
                    className="w-full touch-none"
                    style={{ height: 220 }}
                    data-testid="sign-canvas"
                  />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <Button variant="outline" size="sm" onClick={clearCanvas} data-testid="clear-canvas-btn">
                    <RotateCcw className="w-4 h-4 mr-1" /> Cancella
                  </Button>
                  <p className="text-xs text-gray-500">Disegna la tua firma autografa</p>
                </div>
              </div>

              <div className="flex items-start gap-2 text-xs text-gray-600 border-t pt-3">
                <input
                  type="checkbox"
                  id="consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="w-4 h-4 mt-0.5"
                  data-testid="consent-checkbox"
                />
                <label htmlFor="consent" className="cursor-pointer">
                  Confermo la mia identità e autorizzo l'uso della mia firma per la registrazione della presenza al corso indicato sopra (D.Lgs. 81/2008). I dati saranno trattati ai sensi del GDPR 2016/679.
                </label>
              </div>

              <Button
                onClick={submitSignature}
                disabled={submitting || !consent}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="submit-signature-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {submitting ? 'Invio in corso...' : 'Conferma firma'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
